import { httpsCallable } from "firebase/functions";

import { functions } from "@/firebase/authorityClient";
import { canonicalProfilePublicIdentifier } from "@/utils/profilePublicIdentifier";
import { withUiDeadline } from "@/utils/uiDeadline";

// Version 2 adds ownerPublicIdentifier to each item. The server must support
// version 2 before a client requesting it ships.
const SCHEMA_VERSION = 2;
// Version 3 returns version 2 items without product and service rows, which
// the items authority serves once item reads are live.
const MEDIA_ONLY_SCHEMA_VERSION = 3;
const CATALOGUE_TYPES = new Set(["product", "service"]);
const CATALOGUE_PATH = /^(?:products|services)\//u;
const MAX_LIMIT = 24;
const MAX_ITEMS = 24;
const MAX_CURSOR_LENGTH = 512;
const MAX_SERIALIZED_ITEM_LENGTH = 128_000;
const SCOPES = new Set(["open", "organisations", "organisation-public"]);
const IDENTIFIER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:@-]{0,127}$/;
const ITEM_KEYS = new Set([
  "id",
  "originalPath",
  "createdAt",
  "lastUpdated",
  "organisation",
  "owner",
  "ownerPublicIdentifier",
  "type",
  "mediaType",
  "title",
  "description",
  "price",
  "category",
  "condition",
  "thumbnail",
  "coinSymbol",
  "location",
  "isPublished",
  "images",
  "mediaTypes",
  "mediaThumbnails",
  "originalMediaThumbnails",
  "mediaVariants",
  "mediaThumbnailVariants",
  "originalVideoUrls",
  "variants",
  "imageVariants",
  "thumbnailIndex",
  "mediaUrl",
  "videoUrl",
  "processedVideoUrl",
  "originalVideoUrl",
  "audioUrl",
  "documentUrls",
  "documentMetadata",
  "embedUrl",
  "embedHtml",
  "tags",
  "_timelineId",
  "_originalPath",
]);

export class LegacyMarketplaceFeedCompatibilityError extends Error {
  constructor(code = "unknown") {
    super("The marketplace feed could not be loaded.");
    this.name = "LegacyMarketplaceFeedCompatibilityError";
    this.code = code;
  }
}

const fail = (code) => {
  throw new LegacyMarketplaceFeedCompatibilityError(code);
};

const exactKeys = (value, expected) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const actual = Object.keys(value).sort();
  const wanted = [...expected].sort();
  return (
    actual.length === wanted.length &&
    actual.every((key, index) => key === wanted[index])
  );
};

function normalizeRequest(
  { scope, orgId = null, cursor = null, limit = 12 },
  schemaVersion = SCHEMA_VERSION,
) {
  if (!SCOPES.has(scope)) fail("invalid-argument");
  const scopedToOrganisation = [
    "organisations",
    "organisation-public",
  ].includes(scope);
  if (
    (scopedToOrganisation &&
      (typeof orgId !== "string" || !IDENTIFIER_PATTERN.test(orgId))) ||
    (!scopedToOrganisation && orgId !== null) ||
    !Number.isInteger(limit) ||
    limit < 1 ||
    limit > MAX_LIMIT ||
    (cursor !== null &&
      (typeof cursor !== "string" || cursor.length > MAX_CURSOR_LENGTH))
  ) {
    fail("invalid-argument");
  }
  return Object.freeze({
    schemaVersion,
    scope,
    orgId,
    cursor,
    limit,
  });
}

const isCatalogueItem = (item) =>
  CATALOGUE_TYPES.has(item.type) ||
  [item.originalPath, item._originalPath].some(
    (value) => typeof value === "string" && CATALOGUE_PATH.test(value),
  );

function normalizeItem(value) {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    Object.keys(value).some((key) => !ITEM_KEYS.has(key)) ||
    typeof value.id !== "string" ||
    value.id.length < 1 ||
    value.id.length > 512 ||
    typeof value._timelineId !== "string" ||
    value._timelineId.length < 1 ||
    value._timelineId.length > 512 ||
    typeof value._originalPath !== "string" ||
    value._originalPath.length < 1 ||
    value._originalPath.length > 1024 ||
    // Optional canonical owner identifier: the only value an owner profile
    // link may use (`owner` remains a legacy UID and is never linked).
    (value.ownerPublicIdentifier !== undefined &&
      value.ownerPublicIdentifier !== null &&
      canonicalProfilePublicIdentifier(value.ownerPublicIdentifier) === null)
  ) {
    fail("data-loss");
  }
  let serialized;
  try {
    serialized = JSON.stringify(value);
  } catch (_error) {
    fail("data-loss");
  }
  if (
    typeof serialized !== "string" ||
    serialized.length > MAX_SERIALIZED_ITEM_LENGTH ||
    (value.images !== undefined &&
      (!Array.isArray(value.images) || value.images.length > 50))
  ) {
    fail("data-loss");
  }
  const images =
    Array.isArray(value.images) && value.images.length > 0
      ? value.images
      : typeof value.thumbnail === "string" && value.thumbnail
        ? [value.thumbnail]
        : [];
  return Object.freeze({
    ...value,
    ownerPublicIdentifier: value.ownerPublicIdentifier ?? null,
    itemName: typeof value.title === "string" ? value.title : "",
    itemDescription:
      typeof value.description === "string" ? value.description : "",
    images: Object.freeze([...images]),
    _legacyCompatibilityPresentation: true,
  });
}

function normalizeResponse(value, request) {
  if (
    !exactKeys(value, [
      "schemaVersion",
      "scope",
      "orgId",
      "items",
      "nextCursor",
      "hasMore",
    ]) ||
    value.schemaVersion !== request.schemaVersion ||
    value.scope !== request.scope ||
    value.orgId !== request.orgId ||
    !Array.isArray(value.items) ||
    value.items.length > MAX_ITEMS ||
    typeof value.hasMore !== "boolean" ||
    (value.nextCursor !== null &&
      (typeof value.nextCursor !== "string" ||
        value.nextCursor.length > MAX_CURSOR_LENGTH))
  ) {
    fail("data-loss");
  }
  const items = value.items.map(normalizeItem);
  if (
    request.schemaVersion === MEDIA_ONLY_SCHEMA_VERSION &&
    items.some(isCatalogueItem)
  ) {
    fail("data-loss");
  }
  return Object.freeze({
    items: Object.freeze(items),
    nextCursor: value.nextCursor,
    hasMore: value.hasMore,
  });
}

function normalizeError(error) {
  if (error instanceof LegacyMarketplaceFeedCompatibilityError) return error;
  const code = String(error?.code || "unknown").replace(/^functions\//, "");
  return new LegacyMarketplaceFeedCompatibilityError(code);
}

export function createLegacyMarketplaceFeedCompatibilityService({
  functionsInstance = functions,
  callableFactory = httpsCallable,
} = {}) {
  let callable = null;
  const read = async (options, schemaVersion) => {
    const request = normalizeRequest(options || {}, schemaVersion);
    callable ||= callableFactory(
      functionsInstance,
      "listLegacyMarketplaceFeed",
    );
    try {
      const response = await withUiDeadline(callable(request), {
        phase: "loading marketplace feed",
        timeoutMs: 15_000,
      });
      return normalizeResponse(response?.data, request);
    } catch (error) {
      throw normalizeError(error);
    }
  };
  return Object.freeze({
    listFeed: (options) => read(options, SCHEMA_VERSION),
    // Media posts and NFTs only; products and services come from the items
    // authority while item reads are enabled.
    listMediaOnlyFeed: (options) => read(options, MEDIA_ONLY_SCHEMA_VERSION),
  });
}

export const legacyMarketplaceFeedCompatibilityService =
  createLegacyMarketplaceFeedCompatibilityService();

export {
  normalizeItem as normalizeLegacyMarketplaceFeedItem,
  normalizeRequest as normalizeLegacyMarketplaceFeedRequest,
  normalizeResponse as normalizeLegacyMarketplaceFeedResponse,
};
